/**
 * ⭐ THE COUPLING THE TOGGLE WOULD OTHERWISE HAVE BROKEN: **the brand's accent pair depends on the
 * resolved polarity, so a runtime theme switch has to re-apply the brand.**
 *
 * `cpApplyBranding` chooses `primaryColorDark`/`accentSecondaryDark` over their light siblings from
 * the `resolved` polarity, and its docblock says why that branch exists at all: *"`index.html`
 * deliberately omits `class="dark"` and polarity is resolved at runtime (D-18), so preferring the
 * dark accent unconditionally would paint a dark-tuned accent onto a light surface."*
 *
 * ⛔ Before this component, that call happened EXACTLY ONCE — in `main.tsx`, at boot, for the
 * polarity in force at boot. Adding a toggle without this would have re-created the very defect
 * `cpApplyBranding` was written to avoid, just in the other direction: switch to dark and every
 * accent stays light-tuned, on dark surfaces, for the rest of the session. It is the kind of wrong
 * that reads as "the brand colour looks a bit off in dark mode" rather than as a bug, so it survives.
 *
 * ⚠️ It re-uses `cpApplyBranding` rather than poking `--color-accent` itself. A second implementation
 * of "which accent wins in which polarity" is exactly the drift that produces two answers; the title
 * and favicon writes it also performs are idempotent, so calling the whole function again is free.
 *
 * ⚠️ The brand comes from CONTEXT, not from the module cache, so a `CpBrandingProvider value={…}`
 * override is honoured — the cache-reading form would silently overwrite an injected brand.
 *
 * Renders nothing: it exists to hold an effect at a position in the tree — inside
 * `CpBrandingProvider` (for the brand) and inside `PolarityProvider` (for the polarity).
 */
import { useEffect } from 'react';
import { BASENAME } from '../platform/paths';
import { usePolarityContext } from '../theme/PolarityContext';
import { cpApplyBranding } from './cpApplyBranding';
import { useCpBranding } from './CpBrandingContext';

export function BrandFollowsPolarity(): null {
  const brand = useCpBranding();
  const { resolved } = usePolarityContext();
  useEffect(() => {
    cpApplyBranding(brand, { resolved, basePrefix: BASENAME });
  }, [brand, resolved]);
  return null;
}
